<script setup lang="ts">
import type { PropType } from 'vue';
import { useFieldProps } from '@precon-tools-suite-by-shared/shared-common-forms-ui';
import ProductDisplay from './ProductDisplay.vue';
import Listbox from 'primevue/listbox';
import Dropdown from 'primevue/dropdown';
import type { ProductDisplayFragment } from './ProductDisplay.generated';
import { EntityNameSpan } from '@precon-tools-suite-by-shared/shared-names-ui';

const props = defineProps({
    modelValue: {
        type: [Array, String] as PropType<Array<string> | string>,
        default: () => [],
    },
    label: {
        type: String,
        default: 'Selecteer producten',
    },
    multiple: {
        type: Boolean,
        default: false,
    },
    options: {
        type: Array as PropType<Array<ProductDisplayFragment>>,
        default: () => [],
    },
    dropdown: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits<{
    (e: 'update:modelValue', value: string | Array<string>): void;
}>();

const { id, name, modelValue } = useFieldProps(props, emit);
const filterFields = ['name.full', 'name.short', 'clientRef'];

function idToNode(id: string): ProductDisplayFragment | undefined {
    return props.options.find((x) => x.id === id);
}
</script>

<template>
    <Listbox
        v-if="!dropdown"
        v-bind="$attrs"
        :id="id"
        v-model="modelValue"
        :name="name"
        :options="options"
        option-value="id"
        data-key="id"
        :multiple="multiple"
        :filter="options.length > 10"
        :filter-fields="filterFields"
        :meta-key-selection="false"
        list-style="max-height:89%"
    >
        <template #option="{ option }">
            <ProductDisplay :node="option" horizontal client-ref />
        </template>
    </Listbox>
    <template v-else>
        <MultiSelect
            v-if="multiple"
            v-bind="$attrs"
            :id="id"
            v-model="modelValue"
            :name="name"
            :options="options"
            display="chip"
            option-value="id"
            data-key="id"
            :filter="options.length > 10"
            :filter-fields="filterFields"
        >
            <template #chip="{ value }">
                <EntityNameSpan :node="idToNode(value)" size="short" />
            </template>
            <template #option="{ option }">
                <ProductDisplay :node="option" horizontal client-ref />
            </template>
        </MultiSelect>
        <Dropdown
            v-else
            v-bind="$attrs"
            :id="id"
            v-model="modelValue"
            :name="name"
            :options="options"
            option-value="id"
            data-key="id"
            :filter="options.length > 10"
            :filter-fields="filterFields"
        >
            <template #value="{ value }">
                <EntityNameSpan :node="idToNode(value)" size="short" />
            </template>
            <template #option="{ option }">
                <ProductDisplay :node="option" horizontal client-ref />
            </template>
        </Dropdown>
    </template>
</template>
